import { FrontEnd } from "datatypes/SoramaLocalApi";
import { SorXMetadataFull } from "datatypes/SorXFormat";
import { ProgressIndicator, ProgressType } from "libraries/ProgressIndicator/ProgressIndicator";
import { ICamIV64File } from "services/Hardware/CamHTTP/CamIV64File";
import { CamIV64Service } from "services/Hardware/CamHTTP/CamIV64Service";
import { FirmwareInfo, ICamApi, PackageFileInfo, SpecialMeasurementFileBase } from "services/Hardware/CamHTTP/ICamApi";
import { ResponseMock } from "../../Helper/ResponseMock";
import { BuildingsCamIV64Mock } from "./BuildingsCamIV64Mock";
import { IndustrialCamIV64Mock } from "./IndustrialCamIV64Mock";

export class CamIV64ApiMock implements ICamApi {
    private responseMock: ResponseMock = new ResponseMock();

    constructor(private readonly camIV64Service: CamIV64Service) {}

    //#region Device Management
    public canConnect(): Promise<boolean> {
        return this.responseMock.respond(true);
    }

    public getDeviceId(): Promise<FrontEnd> {
        return this.responseMock.respond({
            id: "mock",
            connectorCount: 1,
            microphoneCount: 64,
            name: "iv64-mock",
        } as FrontEnd);
    }

    public getDeviceConfig(): Promise<unknown> {
        return this.responseMock.fail(new Error("Method not implemented."));
    }

    public getHardwareClock(): Promise<number> {
        return this.responseMock.respond(1674823126);
    }

    public getDeviceFirmware(): Promise<FirmwareInfo> {
        return this.responseMock.respond({ version: "1.2.3", installDate: new Date("10/08/2022") });
    }

    public updateDeviceFirmware(
        firmware: Blob,
        progressCallback: (progressIndicator: ProgressIndicator) => void,
        force?: boolean
    ): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            // Simulate firmware update with realistic progress callbacks
            const firmwareSize = firmware.size || 1024 * 1024; // Default to 1MB if size unknown
            let uploadedBytes = 0;
            const uploadChunkSize = Math.max(firmwareSize / 20, 1024); // Upload in ~20 chunks, min 1KB

            // Phase 1: Upload firmware (60% of total progress)
            const uploadInterval = setInterval(() => {
                uploadedBytes += uploadChunkSize;

                if (uploadedBytes >= firmwareSize) {
                    uploadedBytes = firmwareSize;
                    clearInterval(uploadInterval);

                    // Final upload progress
                    const uploadProgress = new ProgressIndicator(
                        ProgressType.Upload,
                        new ProgressEvent("progress", { loaded: uploadedBytes, total: firmwareSize })
                    );
                    progressCallback(uploadProgress);

                    // Phase 2: Processing firmware (40% of total progress)
                    setTimeout(() => {
                        this.simulateProcessingPhase(progressCallback, resolve, reject);
                    }, 200);
                } else {
                    // Update upload progress
                    const uploadProgress = new ProgressIndicator(
                        ProgressType.Upload,
                        new ProgressEvent("progress", { loaded: uploadedBytes, total: firmwareSize })
                    );
                    progressCallback(uploadProgress);
                }
            }, 150); // Update every 150ms for smooth progress
        });
    }

    public performFactoryReset(): Promise<void> {
        return this.responseMock.respond(null, 10);
    }

    public getLogUrl(): Promise<string> {
        return this.responseMock.respond("/resources/images/icons/FarField.svg");
    }

    //#endregion

    //#region File Management
    public getMeasurementFileStructure(): Promise<ICamIV64File[]> {
        if (this.isIndustrial) {
            return this.responseMock.respond(IndustrialCamIV64Mock.files());
        } else {
            return this.responseMock.respond(BuildingsCamIV64Mock.normalMeasurementFiles());
        }
    }
    public getFavoriteMeasurements(): Promise<ICamIV64File[]> {
        if (this.isIndustrial) {
            return this.responseMock.respond(IndustrialCamIV64Mock.favoriteMeasurements());
        }
    }

    public getSpecialMeasurementFileStructure(): Promise<ICamIV64File[]> {
        if (this.isIndustrial) {
            return this.responseMock.respond([]);
        }

        return this.responseMock.respond(BuildingsCamIV64Mock.specialMeasurementFiles());
    }

    public getMeasurementFileUrl(filePath: string): Promise<string> {
        return this.responseMock.respond("/resources/images/icons/FarField.svg", 0.5);
    }
    public getSpecialMeasurementFileUrl(filePath: string): Promise<string> {
        return this.responseMock.respond("/resources/images/icons/FarField.svg", 0.5);
    }

    public downloadSpecialMeasurementFile(
        filePath: string,
        progressCallback?: (progressIndicator: ProgressIndicator) => void
    ): Promise<SpecialMeasurementFileBase> {
        if (this.isIndustrial) {
            throw new Error("Cannot download special measurement for industrial iV64");
        }
        return this.responseMock.respond(BuildingsCamIV64Mock.downloadSpecialMeasurementFile(filePath));
    }

    public downloadMeasurementFile(
        filePath: string,
        progressCallback?: (progressIndicator: ProgressIndicator) => void
    ): Promise<Blob> {
        // Generate a blob with random data to ensure compression has to work
        const size = 100 * 1024 * 1024; // 100MB is sufficient for testing purposes
        const buffer = new Uint8Array(size);

        // Fill with random data
        for (let i = 0; i < size; i++) {
            buffer[i] = Math.floor(Math.random() * 256);
        }
        const blob = new Blob([buffer], { type: "application/octet-stream" });
        return this.responseMock.respond(blob, 3, 0.1);
    }

    public deleteFileOrFolder(filePath: string): Promise<void> {
        throw new Error("Method not implemented.");
    }

    async getCAMiV64Files(): Promise<ICamIV64File[]> {
        if (this.isIndustrial) {
            const sorXFiles = await this.getMeasurementFileStructure();
            return sorXFiles;
        }
        const [specialMeasurementFiles, measurementFiles] = await Promise.resolve([
            this.getSpecialMeasurementFileStructure(),
            this.getMeasurementFileStructure(),
        ]);
        const files = [...(await specialMeasurementFiles), ...(await measurementFiles)];
        return files;
    }

    //#region Industrial
    public downloadSorXMeasurementMetadata(
        filePath: string,
        progressCallback?: (progressIndicator: ProgressIndicator) => void
    ): Promise<SorXMetadataFull> {
        if (!this.isIndustrial) {
            throw new Error("Cannot download SorX metadata for building iV64");
        }
        return this.responseMock.respond(IndustrialCamIV64Mock.sorxMeasurementMetadata(filePath));
    }
    public downloadSorXMeasurementFile(
        filePath: string,
        progressCallback?: (progressIndicator: ProgressIndicator) => void
    ): Promise<Blob> {
        if (!this.isIndustrial) {
            throw new Error("Cannot download SorX file for building iV64");
        }
        return this.responseMock.respond(IndustrialCamIV64Mock.measurementBlob(filePath));
    }
    //#endregion

    //#region Licensing
    public getLicenseTagsOnDevice(): Promise<string[]> {
        const installedLicenses = ["leak", "mechanical"];
        return this.responseMock.respond(installedLicenses);
    }

    public sendLicenseToDevice(encodedLicensePayload: string): Promise<void> {
        if (!encodedLicensePayload) return this.responseMock.fail(new Error("No license provided"));
        return this.responseMock.respond();
    }
    public sendPackageFileToDevice(encodedPackageFilePayload: PackageFileInfo): Promise<void> {
        if (!encodedPackageFilePayload) return this.responseMock.fail(new Error("No package file provided"));
        return this.responseMock.respond();
    }
    //#endregion

    private get isIndustrial(): boolean {
        return (
            this.camIV64Service.selectedDeviceId.toLowerCase().includes("64s") ||
            this.camIV64Service.selectedDeviceId.toLowerCase().includes("64ex")
        );
    }

    private simulateProcessingPhase(
        progressCallback: (progressIndicator: ProgressIndicator) => void,
        resolve: () => void,
        reject: (error: Error) => void
    ): void {
        const processingSteps = 10;
        let currentStep = 0;

        const processingInterval = setInterval(() => {
            currentStep++;

            // Simulate processing progress
            const processingProgress = new ProgressIndicator(
                ProgressType.Processing,
                new ProgressEvent("progress", { loaded: currentStep, total: processingSteps })
            );
            progressCallback(processingProgress);

            if (currentStep >= processingSteps) {
                clearInterval(processingInterval);

                // Check if we should simulate a failure (10% chance for testing)
                if (Math.random() < 0.1) {
                    reject(new Error("Firmware update failed during processing"));
                } else {
                    // Success - firmware update completed
                    resolve();
                }
            }
        }, 300); // Update every 300ms for processing phase
    }
}
